// src/composables/useLogin.ts
import { ref } from "vue";
import { useRouter } from "@/router";
import { useStore } from "@/store";
import { RouterName } from "@/enums/RouterEnum";
import { getUserInfo } from "@/api/login";
import { getPrivateKey } from "@/components/getPrivateKey";
import { setPhoneNumber } from "@/components/setPhoneNum/";
import { getChatInitData } from "@/components/FloatingCustomerService/chatService";
import type { ChatControls } from "@/components/FloatingCustomerService/useChatControls";
import { closeRemind } from "@/api/dataset";
import { refreshCasdoorToken } from "@/plugins/casdoor";

export const useAuth = (chatControls: ChatControls) => {
  const store = useStore() as any;
  const router = useRouter();
  const bindFlag = ref(true);
  const IS_ELECTRON = process.env.IS_ELECTRON;

  const getReturnUrl = (): string => {
    // 先从 URL 中获取
    const returnUrlIndex = window.location.href.indexOf("returnUrl=");
    const urlReturnUrl =
      returnUrlIndex > -1
        ? window.location.href.substring(returnUrlIndex + 10)
        : "";

    // 如果 URL 中没有，则从 localStorage 中获取
    const storageReturnUrl = localStorage.getItem("returnUrl") || "";

    // 删除 localStorage 中的 returnUrl
    localStorage.removeItem("returnUrl");

    // 优先使用 URL 中的 returnUrl，如果没有则使用 localStorage 中的
    return urlReturnUrl || storageReturnUrl;
  };

  const handleNavigation = (returnUrl: string) => {
    if (returnUrl.length > 0) {
      try {
        let path: string;

        if (
          returnUrl.startsWith("http://") ||
          returnUrl.startsWith("https://")
        ) {
          const url = new URL(returnUrl);
          if (url.origin === window.location.origin) {
            if (returnUrl.indexOf("discourse") === -1) {
              path = url.pathname + url.search + url.hash;
            } else {
              window.location.replace(returnUrl);
              return;
            }
          } else {
            window.location.href = returnUrl;
            return;
          }
        } else if (returnUrl.startsWith("/")) {
          path = returnUrl;
        } else {
          path = "/" + returnUrl;
        }

        router.replace(path);
      } catch (error) {
        console.error("Invalid return URL:", error);
      }
    } else {
      if (IS_ELECTRON) {
        router.push({ name: RouterName.WELCOME });
      } else {
        router.push({ name: RouterName.RECOMMEND });
      }
    }
  };

  const setUser = async () => {
    await store.dispatch("user/getUserPersonalInfo");
    bindFlag.value = true;

    // 重置任务状态
    if (IS_ELECTRON) {
      try {
        await window.electronAPI.resetTaskStatus();
      } catch (error) {
        console.error("Error resetting task status:", error);
      }
    }

    const returnUrl = getReturnUrl();
    handleNavigation(returnUrl);

    // const token = await getChatInitData();
    // chatControls.updateToken(token);
  };

  const loginSuccess = async () => {
    const res = await getUserInfo();
    if (localStorage.getItem("hidePopup")) {
      closeRemind();
      localStorage.removeItem("hidePopup");
    }
    // if (!res.bindStatus) {
    //   bindFlag.value = false;
    //   try {
    //     const result = await setPhoneNumber({ smsType: 1 });
    //     if (result.canceled) {
    //       console.log("User canceled the operation");
    //     }
    //   } catch (error) {
    //     console.error("An error occurred:", error);
    //   }
    // }
    setUser();
    console.log("res:", res);
    if (!res.privateKey) {
      console.log("开始绑定验证码");
      bindFlag.value = false;
      getPrivateKey();
    }
    return res;
  };

  // 检查token是否过期
  const checkTokenExpiration = () => {
    const token = localStorage.getItem("token");
    if (!token) return true;

    try {
      // 检查 token 是否符合 JWT 格式（包含两个点）
      if (!token.includes(".") || token.split(".").length !== 3) {
        console.error("Invalid token format");
        return true;
      }

      const parts = token.split(".");
      const payload = parts[1];
      console.log("Token payload before processing:", payload);

      // 处理 URL-safe base64
      // 1. 替换 URL-safe 字符
      const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");

      // 2. 添加填充
      const paddedBase64 = base64.padEnd(
        base64.length + ((4 - (base64.length % 4)) % 4),
        "="
      );

      console.log("Processed base64 payload:", paddedBase64);

      try {
        const decodedPayload = atob(paddedBase64);
        const tokenData = JSON.parse(decodedPayload);
        console.log("Decoded token data:", tokenData);

        if (!tokenData.exp) {
          console.error("Token missing expiration time");
          return true;
        }

        const expirationTime = tokenData.exp * 1000; // 转换为毫秒
        // 如果token将在24小时内过期，也视为需要刷新
        return Date.now() >= expirationTime - 24 * 60 * 60 * 1000;
      } catch (decodeError) {
        console.error("Error decoding token payload:", decodeError);
        return true;
      }
    } catch (error) {
      console.error("Error parsing token:", error);
      return true;
    }
  };

  // 刷新token
  const refreshTokenIfNeeded = async () => {
    try {
      if (checkTokenExpiration()) {
        console.log("Token expired or about to expire, refreshing...");
        const result = await refreshCasdoorToken();
        if (!result || !result.access_token) {
          console.error("Token refresh failed");
          // token刷新失败，清除登录状态
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("isLogin");
          store.dispatch("user/logout");
          return false;
        }
        console.log("Token refreshed successfully");
        return true;
      }
      return true;
    } catch (error) {
      console.error("Error during token refresh:", error);
      return false;
    }
  };

  return {
    bindFlag,
    loginSuccess,
    refreshTokenIfNeeded,
  };
};
